import some from 'lodash/some';
import concat from 'lodash/concat';
import reject from 'lodash/reject';
import orderBy from 'lodash/orderBy';
import uniqWith from 'lodash/uniqWith';
import { hasPoints, isEqualTotalScore } from './Score';
import type { TotalScore } from './Score';

export interface HighscoreEntry {
	playerName: string;
	score: TotalScore;
	synced: boolean;
}

export const isEqualHighscoreEntry = (
	entry1: HighscoreEntry,
	entry2: HighscoreEntry
): boolean => {
	return (
		entry1.playerName === entry2.playerName &&
		isEqualTotalScore(entry1.score, entry2.score)
	);
};

export const containsHighscoreEntry = (
	entries: HighscoreEntry[],
	playerName: string,
	score: TotalScore
): boolean => {
	return some(entries, (entry) => {
		return (
			entry.playerName === playerName && isEqualTotalScore(entry.score, score)
		);
	});
};

export const canAddHighscoreEntries = (
	entries: HighscoreEntry[],
	playerName: string,
	score: TotalScore
): boolean =>
	hasPoints(score) && !containsHighscoreEntry(entries, playerName, score);

export const getNotSyncedHighscoreEntries = (
	entries: HighscoreEntry[]
): HighscoreEntry[] => {
	return reject(entries, (entry) => entry.synced);
};

/**
 * Merges two lists of high score entries, removing duplicates and keeping the synced status.
 */
export const mergeHighscore = (
	entries: HighscoreEntry[],
	additionalEntries: HighscoreEntry[]
): HighscoreEntry[] => {
	const concatenated = concat(entries, additionalEntries);

	const filtered = reject(
		concatenated,
		(concatenatedEntry) => !hasPoints(concatenatedEntry.score)
	);

	// Sorted by synced in descending order, so that the synced entry is prioritized later when making the highscore unique.
	const ordered = orderBy(
		filtered,
		[
			'playerName',
			'score.environment',
			'score.happiness',
			'score.qualityOfLife',
			'synced'
		],
		['asc', 'asc', 'asc', 'asc', 'desc']
	);

	return uniqWith(ordered, isEqualHighscoreEntry);
};
